import React from "react";
import styled, { keyframes, css } from "styled-components";
import { Mic, Square } from "lucide-react";

interface MicVisualizerProps {
  volume: number; // 0~1
  isRecording: boolean;
  onToggle: () => void;
}

const MicVisualizer: React.FC<MicVisualizerProps> = ({
  volume,
  isRecording,
  onToggle,
}) => {
  // 볼륨 강도 (부드러운 반응)
  const v = Math.min(1, Math.max(0, volume));
  const intensity = Math.pow(v, 0.6);

  return (
    <Container>
      {/* 외부 글로우 효과 */}
      <OuterGlow $isRecording={isRecording} $volume={intensity} />
      
      {/* 중간 링 */}
      <MiddleRing $isRecording={isRecording} $volume={intensity} />
      
      {/* 내부 링 */}
      <InnerRing $isRecording={isRecording} $volume={intensity} />
      
      {/* 메인 버튼 */}
      <MainButton onClick={onToggle} $isRecording={isRecording} $volume={intensity}>
        <ButtonInner $isRecording={isRecording}>
          {isRecording ? (
            <Square size={24} color="white" />
          ) : (
            <Mic size={24} color="white" />
          )}
        </ButtonInner>
      </MainButton>
    </Container>
  );
};

export default MicVisualizer;

/* ---------------------------- styled components ---------------------------- */

const Container = styled.div`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 부드러운 펄스 애니메이션
const pulse = keyframes`
  0%, 100% { 
    transform: scale(1); 
    opacity: 0.8; 
  }
  50% { 
    transform: scale(1.1); 
    opacity: 0.4; 
  }
`;

// 녹음 중 글로우 효과
const recordingGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(239, 68, 68, 0.3);
  }
  50% { 
    box-shadow: 0 0 40px rgba(239, 68, 68, 0.6);
  }
`;

// 외부 글로우 (가장 큰 원)
const OuterGlow = styled.div<{ $isRecording: boolean; $volume: number }>`
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: ${({ $isRecording }) =>
    $isRecording
      ? "radial-gradient(circle, rgba(239, 68, 68, 0.15) 0%, transparent 70%)"
      : "radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)"};
  
  ${({ $isRecording, $volume }) =>
    $isRecording &&
    css`
      animation: ${pulse} 2s ease-in-out infinite;
    `};
  
  transform: ${({ $volume }) => `scale(${1 + $volume * 0.3})`};
  opacity: ${({ $volume }) => 0.3 + $volume * 0.4};
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
`;

// 중간 링
const MiddleRing = styled.div<{ $isRecording: boolean; $volume: number }>`
  position: absolute;
  width: 140px;
  height: 140px;
  border-radius: 50%;
  border: 2px solid ${({ $isRecording }) =>
    $isRecording ? "rgba(239, 68, 68, 0.3)" : "rgba(59, 130, 246, 0.3)"};
  
  ${({ $isRecording, $volume }) =>
    $isRecording &&
    css`
      animation: ${pulse} 2s ease-in-out infinite 0.3s;
    `};
  
  transform: ${({ $volume }) => `scale(${1 + $volume * 0.2})`};
  opacity: ${({ $volume }) => 0.4 + $volume * 0.3};
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
`;

// 내부 링
const InnerRing = styled.div<{ $isRecording: boolean; $volume: number }>`
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 1px solid ${({ $isRecording }) =>
    $isRecording ? "rgba(239, 68, 68, 0.4)" : "rgba(59, 130, 246, 0.4)"};
  
  ${({ $isRecording, $volume }) =>
    $isRecording &&
    css`
      animation: ${pulse} 2s ease-in-out infinite 0.6s;
    `};
  
  transform: ${({ $volume }) => `scale(${1 + $volume * 0.15})`};
  opacity: ${({ $volume }) => 0.5 + $volume * 0.2};
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
`;

// 메인 버튼
const MainButton = styled.button<{ $isRecording: boolean; $volume: number }>`
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  outline: none;
  cursor: pointer;
  z-index: 10;
  
  background: ${({ $isRecording }) =>
    $isRecording
      ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
      : "linear-gradient(135deg, #7a8bff 0%, #5c63ff 100%)"};
  
  box-shadow: ${({ $isRecording, $volume }) =>
    $isRecording
      ? `0 8px 32px rgba(239, 68, 68, ${0.3 + $volume * 0.2}), 
         0 0 0 1px rgba(239, 68, 68, 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.2)`
      : `0 8px 32px rgba(59, 130, 246, ${0.2 + $volume * 0.15}), 
         0 0 0 1px rgba(59, 130, 246, 0.1),
         inset 0 1px 0 rgba(255, 255, 255, 0.2)`};
  
  transform: ${({ $volume }) => `scale(${1 + $volume * 0.1})`};
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    transform: ${({ $volume }) => `scale(${1.05 + $volume * 0.1})`};
    box-shadow: ${({ $isRecording }) =>
      $isRecording
        ? `0 12px 40px rgba(239, 68, 68, 0.4), 
           0 0 0 1px rgba(239, 68, 68, 0.2),
           inset 0 1px 0 rgba(255, 255, 255, 0.3)`
        : `0 12px 40px rgba(59, 130, 246, 0.3), 
           0 0 0 1px rgba(59, 130, 246, 0.2),
           inset 0 1px 0 rgba(255, 255, 255, 0.3)`};
  }
  
  &:active {
    transform: ${({ $volume }) => `scale(${0.98 + $volume * 0.1})`};
  }
`;

// 버튼 내부 컨테이너
const ButtonInner = styled.div<{ $isRecording: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  
  svg {
    transition: transform 0.15s cubic-bezier(0.4, 0, 0.2, 1);
    transform: ${({ $isRecording }) => ($isRecording ? "scale(0.9)" : "scale(1)")};
  }
`;